import {
  GraphFilterTimeRangeOptions,
  GraphFilterViewTypeOptions,
} from './graph-filter.enum';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { Overlay, OverlayModule, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  addDays,
  addMonths,
  addQuarters,
  addWeeks,
  addYears,
  differenceInCalendarDays,
  endOfDay,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  format,
  getISOWeek,
  isValid,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
} from 'date-fns';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzSegmentedModule } from 'ng-zorro-antd/segmented';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { Subject, takeUntil } from 'rxjs';
import { GraphFilterEvent } from '../../insights-data.interface';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { ReportParams } from '../../../view-bip-case/report-manager/report-manager.interface';
import { TimeFilter, TimeFilterType } from './graph-filter.interface';
import { SvgIconComponent } from 'angular-svg-icon';

type DateRangeArray = (Date | string | null)[];
@Component({
  selector: 'app-graph-filter',
  standalone: true,
  imports: [
    NzFormModule,
    NzSegmentedModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NzDatePickerModule,
    NzSelectModule,
    NzSwitchModule,
    NzButtonModule,
    OverlayModule,
    SvgIconComponent,
  ],
  templateUrl: './graph-filter.component.html',
  styleUrls: ['./graph-filter.component.scss'],
})
export class GraphFilterComponent
  implements OnInit, OnDestroy, OnChanges, AfterViewInit
{
  @Input() graphFilterData!: ReportParams;
  @Input() resetFilter = false;
  @Output() filterChange = new EventEmitter<GraphFilterEvent>();
  @Output() timeFilterChange = new EventEmitter<TimeFilter>();

  @ViewChild('popoverTpl') private readonly popoverTpl!: TemplateRef<void>;
  @ViewChild('dateBarTrigger')
  private readonly dateBarTrigger!: ElementRef<HTMLElement>;

  private readonly overlay = inject(Overlay);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private overlayRef: OverlayRef | null = null;

  private readonly destroy$ = new Subject<void>();

  timeRangeOptions = GraphFilterTimeRangeOptions;
  viewTypeOptions = GraphFilterViewTypeOptions;
  enabledViewTypeOptions = GraphFilterViewTypeOptions;
  filterForm!: FormGroup;

  dateFormat = 'MM/dd/yyyy';
  DATE_FORMAT = 'yyyy-MM-dd';

  private readonly defaultTimeOption = 1;

  // Popover temp state
  tempTimeOption = 1;
  tempSingleDate: Date = new Date();
  tempDateRange: Date[] = [new Date(), new Date()];

  constructor(
    private readonly fb: FormBuilder,
    private readonly cd: ChangeDetectorRef
  ) {
    const today = new Date();
    const initialRange = this.calculateRangeByType('week', today);

    this.filterForm = this.fb.group({
      timeOption: [this.defaultTimeOption, Validators.required],
      singleDate: [today, Validators.required],
      filterDateRange: [
        [initialRange.startDate, initialRange.endDate],
        Validators.required,
      ],
      viewType: [1],
      isTrendLineEnabled: [false],
    });
  }

  get isRangeTypeSelected(): boolean {
    return this.filterForm.get('timeOption')?.value === 5;
  }

  get selectedTimeType(): TimeFilterType {
    return this.mapTimeOptionToType(this.filterForm.get('timeOption')?.value);
  }

  get singlePickerMode(): 'date' | 'week' | 'month' | 'quarter' | 'year' {
    switch (this.selectedTimeType) {
      case 'week':
        return 'week';
      case 'month':
        return 'month';
      case 'quarter':
        return 'quarter';
      case 'year':
        return 'year';
      case 'day':
      default:
        return 'date';
    }
  }

  get pickerDisplayLabel(): string {
    const singleDate = this.toDate(
      this.filterForm.get('singleDate')?.value ?? null
    );
    const dateRange = this.filterForm.get('filterDateRange')
      ?.value as DateRangeArray;

    if (this.selectedTimeType === 'range' && this.isValidDateRange(dateRange)) {
      const startDate = this.toDate(dateRange[0]);
      const endDate = this.toDate(dateRange[1]);
      if (!startDate || !endDate) return '';
      return `${format(startDate, 'MM/dd/yyyy')} - ${format(endDate, 'MM/dd/yyyy')}`;
    }

    if (!singleDate) return '';

    switch (this.selectedTimeType) {
      case 'week':
        return `Week of ${format(startOfWeek(singleDate, { weekStartsOn: 1 }), 'MM/dd/yyyy')}`;
      case 'month':
        return format(singleDate, 'MMM yyyy');
      case 'quarter':
        return `Q${Math.floor(singleDate.getMonth() / 3) + 1} ${format(singleDate, 'yyyy')}`;
      case 'year':
        return format(singleDate, 'yyyy');
      case 'day':
      default:
        return format(singleDate, 'MM/dd/yyyy');
    }
  }

  get selectedRangeDisplay(): string {
    const range = this.normalizeRange(
      this.filterForm.get('filterDateRange')?.value as DateRangeArray
    );

    if (!range) return '';
    const [startDate, endDate] = range;

    if (this.selectedTimeType === 'day') {
      return format(startDate, 'MM/dd/yyyy');
    }

    return `${format(startDate, 'MM/dd/yyyy')} to ${format(endDate, 'MM/dd/yyyy')}`;
  }

  get selectedRangeMetaLabel(): string {
    const range = this.normalizeRange(
      this.filterForm.get('filterDateRange')?.value as DateRangeArray
    );
    if (!range) return '';

    const [startDate] = range;
    if (this.selectedTimeType === 'week') {
      return `WEEK - ${getISOWeek(startDate)}`;
    }

    return this.selectedTimeType.toUpperCase();
  }

  ngAfterViewInit(): void {
    this.filterForm
      .get('timeOption')
      ?.setValue(this.defaultTimeOption, { emitEvent: false });
  }

  ngOnInit(): void {
    // Emit initial values
    const dateRange = this.filterForm.value.filterDateRange;
    if (this.isValidDateRange(dateRange)) {
      this.updateViewTypeOptionsByDateRange(dateRange);
      this.emitFilterChange(dateRange);
    }

    // Subscribe to filterDateRange changes
    this.filterForm
      .get('filterDateRange')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((dates: DateRangeArray) => {
        if (!this.isRangeTypeSelected) return;
        const normalizedRange = this.normalizeRange(dates);
        if (!normalizedRange) return;

        this.filterForm.patchValue(
          { filterDateRange: normalizedRange },
          { emitEvent: false }
        );
        this.updateViewTypeOptionsByDateRange(normalizedRange);
        this.emitFilterChange(normalizedRange);
      });

    this.filterForm
      .get('singleDate')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((singleDate: Date | null) => {
        if (this.isRangeTypeSelected || !singleDate) return;
        const calculatedRange = this.calculateRangeByType(
          this.selectedTimeType,
          singleDate
        );
        this.filterForm.patchValue(
          {
            filterDateRange: [
              calculatedRange.startDate,
              calculatedRange.endDate,
            ],
          },
          { emitEvent: false }
        );
        this.updateViewTypeOptionsByDateRange([
          calculatedRange.startDate,
          calculatedRange.endDate,
        ]);
        this.emitFilterChange([
          calculatedRange.startDate,
          calculatedRange.endDate,
        ]);
      });

    // Subscribe to viewType changes
    this.filterForm
      .get('viewType')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(viewType => {
        this.filterForm.patchValue({ viewType }, { emitEvent: false });
        const dates = this.filterForm.value.filterDateRange;
        if (!this.isValidDateRange(dates)) return;
        this.emitFilterChange(dates);
      });

    // Subscribe to isTrendLineEnabled changes
    this.filterForm
      .get('isTrendLineEnabled')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(isTrendLineEnabled => {
        this.filterForm.patchValue(
          { isTrendLineEnabled },
          { emitEvent: false }
        );
        const dates = this.filterForm.value.filterDateRange;
        if (!this.isValidDateRange(dates)) return;
        this.emitFilterChange(dates);
      });
    this.filterForm
      .get('timeOption')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        this.onTimeRangeChange(value);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['graphFilterData']) {
      this.setFormData();
    }
    if (changes['resetFilter']?.currentValue === true) {
      this.resetForm();
    }
  }
  resetForm(): void {
    const today = new Date();
    const initialRange = this.calculateRangeByType('week', today);
    this.filterForm.patchValue(
      {
        timeOption: this.defaultTimeOption,
        singleDate: today,
        filterDateRange: [initialRange.startDate, initialRange.endDate],
        viewType: 1,
        isTrendLineEnabled: false,
      },
      { emitEvent: false }
    );

    this.onTimeRangeChange(this.defaultTimeOption);
  }
  ngOnDestroy(): void {
    this.overlayRef?.dispose();
    this.destroy$.next();
    this.destroy$.complete();
  }

  setFormData(): void {
    if (!this.graphFilterData) return;

    const {
      startDate,
      endDate,
      viewType,
      isTrendLineEnabled,
      selectedTimeOption,
    } = this.graphFilterData;

    // Handle both Date and string for startDate/endDate
    const parsedStart = this.toDate(startDate ?? null);
    const parsedEnd = this.toDate(endDate ?? null);
    if (!parsedStart || !parsedEnd) return;

    // Fallbacks for viewType and selectedTimeOption
    const safeViewType = typeof viewType === 'number' ? viewType : 1;
    const safeTimeOption =
      typeof selectedTimeOption === 'number' ? selectedTimeOption : 5;

    const current = this.filterForm.value;
    const noChange =
      current.viewType === safeViewType &&
      current.isTrendLineEnabled === isTrendLineEnabled &&
      current.timeOption === safeTimeOption &&
      current.filterDateRange?.[0]?.toString() === parsedStart.toString() &&
      current.filterDateRange?.[1]?.toString() === parsedEnd.toString();
    if (noChange) return;

    // Always patch all fields to force update
    const singleDate = parsedEnd;
    this.filterForm.patchValue(
      {
        filterDateRange: [parsedStart, parsedEnd],
        singleDate,
        viewType: safeViewType,
        isTrendLineEnabled,
        timeOption: safeTimeOption,
      },
      { emitEvent: false }
    );
    this.cd.detectChanges();
    this.updateViewTypeOptionsByDateRange([parsedStart, parsedEnd]);
  }

  isValidDateRange(dateRange: DateRangeArray): boolean {
    return (
      Array.isArray(dateRange) &&
      dateRange.length === 2 &&
      this.toDate(dateRange[0]) !== null &&
      this.toDate(dateRange[1]) !== null
    );
  }

  updateViewTypeOptionsByDateRange(dateRange: DateRangeArray): void {
    const msInDay = 1000 * 60 * 60 * 24;
    const normalizedRange = this.normalizeRange(dateRange);
    if (!normalizedRange) {
      for (const opt of this.viewTypeOptions) {
        opt.isEnabled = true;
      }
      return;
    }

    const [start, end] = normalizedRange;
    const diffDays = Math.floor((end.getTime() - start.getTime()) / msInDay);

    for (const opt of this.viewTypeOptions) {
      switch (opt.value) {
        case 1:
          opt.isEnabled = true;
          break;
        case 2:
          opt.isEnabled = diffDays >= 7;
          break;
        case 3:
          opt.isEnabled = diffDays >= 60;
          break;
        case 4:
          opt.isEnabled = diffDays > 180;
          break;
        case 5:
          opt.isEnabled = diffDays > 365;
          break;
        default:
          opt.isEnabled = true;
      }
    }

    this.enabledViewTypeOptions = this.viewTypeOptions.filter(
      opt => opt.isEnabled
    );

    // Ensure viewType is valid
    const currentViewType = this.filterForm.value.viewType;
    const isValidViewType = this.enabledViewTypeOptions.some(
      opt => opt.value === currentViewType
    );

    if (!isValidViewType) {
      this.filterForm.patchValue(
        {
          viewType: this.enabledViewTypeOptions[0]?.value || null,
        },
        { emitEvent: false } // Prevent triggering valueChanges
      );
    }
  }

  onTimeRangeChange(value: number): void {
    const selectedType = this.mapTimeOptionToType(value);
    const currentSingleDate = this.toDate(
      this.filterForm.get('singleDate')?.value ?? null
    );
    const baseDate = currentSingleDate ?? new Date();
    const nextRange =
      selectedType === 'range'
        ? (this.normalizeRange(
            this.filterForm.get('filterDateRange')?.value as DateRangeArray
          ) ?? [startOfMonth(baseDate), endOfMonth(baseDate)])
        : this.rangeToArray(this.calculateRangeByType(selectedType, baseDate));

    const [startDate, endDate] = nextRange;

    this.filterForm.patchValue(
      {
        timeOption: value,
        singleDate: baseDate,
        filterDateRange: [startDate, endDate],
      },
      { emitEvent: false } // Prevent triggering valueChanges
    );

    this.updateViewTypeOptionsByDateRange([startDate, endDate]);
    this.emitFilterChange([startDate, endDate]);
  }

  onNavigateRange(direction: 'prev' | 'next'): void {
    const multiplier = direction === 'next' ? 1 : -1;
    const currentRange = this.normalizeRange(
      this.filterForm.get('filterDateRange')?.value as DateRangeArray
    );

    if (!currentRange) return;

    const [currentStart, currentEnd] = currentRange;
    let nextStart: Date;
    let nextEnd: Date;

    switch (this.selectedTimeType) {
      case 'day':
        nextStart = addDays(currentStart, multiplier);
        nextEnd = endOfDay(nextStart);
        break;
      case 'week':
        nextStart = addWeeks(currentStart, multiplier);
        nextEnd = endOfWeek(nextStart, { weekStartsOn: 1 });
        break;
      case 'month': {
        const shifted = addMonths(currentStart, multiplier);
        const range = this.calculateRangeByType('month', shifted);
        nextStart = range.startDate;
        nextEnd = range.endDate;
        break;
      }
      case 'quarter': {
        const shifted = addQuarters(currentStart, multiplier);
        const range = this.calculateRangeByType('quarter', shifted);
        nextStart = range.startDate;
        nextEnd = range.endDate;
        break;
      }
      case 'year': {
        const shifted = addYears(currentStart, multiplier);
        const range = this.calculateRangeByType('year', shifted);
        nextStart = range.startDate;
        nextEnd = range.endDate;
        break;
      }
      case 'range':
      default: {
        const selectedDays =
          differenceInCalendarDays(currentEnd, currentStart) + 1;
        nextStart = addDays(currentStart, selectedDays * multiplier);
        nextEnd = addDays(currentEnd, selectedDays * multiplier);
        break;
      }
    }

    const normalizedNextStart = startOfDay(nextStart);
    const normalizedNextEnd = endOfDay(nextEnd);

    this.filterForm.patchValue(
      {
        singleDate: normalizedNextEnd,
        filterDateRange: [normalizedNextStart, normalizedNextEnd],
      },
      { emitEvent: false }
    );

    this.updateViewTypeOptionsByDateRange([
      normalizedNextStart,
      normalizedNextEnd,
    ]);
    this.emitFilterChange([normalizedNextStart, normalizedNextEnd]);
  }

  handleViewType(): void {
    const dateRange = this.filterForm.value.filterDateRange;
    this.updateViewTypeOptionsByDateRange(dateRange);
    this.cd.markForCheck();
  }

  /* ---- Popover methods ---- */

  togglePopover(): void {
    if (this.overlayRef?.hasAttached()) {
      this.cancelPopover();
      return;
    }
    // Snapshot current state into temp variables
    this.tempTimeOption =
      this.filterForm.get('timeOption')?.value ?? this.defaultTimeOption;
    this.tempSingleDate =
      this.toDate(this.filterForm.get('singleDate')?.value ?? null) ??
      new Date();
    const range = this.filterForm.get('filterDateRange')?.value;
    this.tempDateRange =
      Array.isArray(range) && range.length === 2
        ? [
            this.toDate(range[0]) ?? new Date(),
            this.toDate(range[1]) ?? new Date(),
          ]
        : [new Date(), new Date()];

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.dateBarTrigger)
      .withPositions([
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
          offsetY: 8,
        },
        {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom',
          offsetY: -8,
        },
      ]);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      minWidth: 320,
    });

    this.overlayRef
      .backdropClick()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.cancelPopover());

    const portal = new TemplatePortal(this.popoverTpl, this.viewContainerRef);
    this.overlayRef.attach(portal);
  }

  cancelPopover(): void {
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }

  applyPopover(): void {
    const isRange = this.tempTimeOption === 5;
    const baseDate = this.tempSingleDate ?? new Date();
    const tempType = this.mapTimeOptionToType(this.tempTimeOption);

    let startDate: Date;
    let endDate: Date;

    if (isRange) {
      const normalizedTempRange = this.normalizeRange(this.tempDateRange);
      if (normalizedTempRange) {
        [startDate, endDate] = normalizedTempRange;
      } else {
        const fallbackRange = this.calculateRangeByType('month', new Date());
        startDate = fallbackRange.startDate;
        endDate = fallbackRange.endDate;
      }
    } else {
      const calculated = this.calculateRangeByType(tempType, baseDate);
      startDate = calculated.startDate;
      endDate = calculated.endDate;
    }

    this.filterForm.patchValue(
      {
        timeOption: this.tempTimeOption,
        singleDate: baseDate,
        filterDateRange: [startOfDay(startDate), endOfDay(endDate)],
      },
      { emitEvent: false }
    );

    this.updateViewTypeOptionsByDateRange([startDate, endDate]);
    this.emitFilterChange([startDate, endDate]);
    this.cancelPopover();
  }

  onPopoverTabChange(value: number): void {
    this.tempTimeOption = value;
    // Recalculate temp dates for the new type
    const baseDate = this.tempSingleDate ?? new Date();
    const type = this.mapTimeOptionToType(value);
    if (type !== 'range') {
      const range = this.calculateRangeByType(type, baseDate);
      this.tempDateRange = [range.startDate, range.endDate];
    }
  }

  getTempPickerMode(): 'date' | 'week' | 'month' | 'quarter' | 'year' {
    const type = this.mapTimeOptionToType(this.tempTimeOption);
    switch (type) {
      case 'week':
        return 'week';
      case 'month':
        return 'month';
      case 'quarter':
        return 'quarter';
      case 'year':
        return 'year';
      case 'day':
      default:
        return 'date';
    }
  }

  get currentPeriodLabel(): string {
    const type = this.mapTimeOptionToType(this.tempTimeOption);
    switch (type) {
      case 'day':
        return 'Today';
      case 'week':
        return 'Current Week';
      case 'month':
        return 'Current Month';
      case 'quarter':
        return 'Current Quarter';
      case 'year':
        return 'Current Year';
      case 'range':
        return 'Current Month';
      default:
        return 'Today';
    }
  }

  goToCurrentPeriod(): void {
    const today = new Date();
    this.tempSingleDate = today;
    const type = this.mapTimeOptionToType(this.tempTimeOption);
    const range = this.calculateRangeByType(
      type === 'range' ? 'month' : type,
      today
    );
    this.tempDateRange = [range.startDate, range.endDate];
  }

  private emitFilterChange(dates: DateRangeArray): void {
    const normalizedRange = this.normalizeRange(dates);
    if (!normalizedRange) return;

    const [startDate, endDate] = normalizedRange;

    const type = this.selectedTimeType;
    const normalizedStart = startOfDay(startDate);
    const normalizedEnd = endOfDay(endDate);

    this.timeFilterChange.emit({
      type,
      startDate: normalizedStart,
      endDate: normalizedEnd,
    });

    this.filterChange.emit({
      startDate: format(startDate, this.DATE_FORMAT),
      endDate: format(endDate, this.DATE_FORMAT),
      viewType: this.filterForm.value.viewType,
      isTrendLineEnabled: this.filterForm.value.isTrendLineEnabled,
      selectedTimeOption: this.filterForm.value.timeOption ?? null,
      isCustomDateRange: this.isRangeTypeSelected,
    });
  }

  private mapTimeOptionToType(
    option: number | null | undefined
  ): TimeFilterType {
    const selectedOption = this.timeRangeOptions.find(
      item => item.value === option
    );
    return selectedOption?.type ?? 'month';
  }

  private calculateRangeByType(type: TimeFilterType, date: Date): TimeFilter {
    switch (type) {
      case 'day':
        return {
          type,
          startDate: startOfDay(date),
          endDate: endOfDay(date),
        };
      case 'week': {
        const weekStart = startOfWeek(date, { weekStartsOn: 1 });
        const weekEnd = endOfWeek(date, { weekStartsOn: 1 });
        return {
          type,
          startDate: startOfDay(weekStart),
          endDate: endOfDay(weekEnd),
        };
      }
      case 'month':
        return {
          type,
          startDate: startOfDay(startOfMonth(date)),
          endDate: endOfDay(endOfMonth(date)),
        };
      case 'quarter':
        return {
          type,
          startDate: startOfDay(startOfQuarter(date)),
          endDate: endOfDay(endOfQuarter(date)),
        };
      case 'year':
        return {
          type,
          startDate: startOfDay(startOfYear(date)),
          endDate: endOfDay(endOfYear(date)),
        };
      case 'range':
      default:
        return {
          type: 'range',
          startDate: startOfDay(date),
          endDate: endOfDay(date),
        };
    }
  }

  private rangeToArray(range: TimeFilter): [Date, Date] {
    return [range.startDate, range.endDate];
  }

  private normalizeRange(range: DateRangeArray): [Date, Date] | null {
    if (!this.isValidDateRange(range)) return null;

    const startDate = this.toDate(range[0]);
    const endDate = this.toDate(range[1]);

    if (!startDate || !endDate) return null;

    const minDate = new Date(Math.min(startDate.getTime(), endDate.getTime()));
    const maxDate = new Date(Math.max(startDate.getTime(), endDate.getTime()));
    const normalizedStart = startOfDay(minDate);
    const normalizedEnd = endOfDay(maxDate);
    return [normalizedStart, normalizedEnd];
  }

  private toDate(value: Date | string | null): Date | null {
    if (!value) return null;
    if (value instanceof Date) {
      return isValid(value) ? value : null;
    }

    const parsedDate = new Date(value);
    return isValid(parsedDate) ? parsedDate : null;
  }
}
